import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import {
  TEMPLATE_KINDS,
  type TemplateKind,
  type TemplateSummary,
  useDeleteTemplate,
  useDuplicateTemplate,
  useTemplates,
  useUpdateTemplate,
} from '@/entities/template';
import { isApiError } from '@/shared/api';
import { FormError } from '@/shared/forms';
import {
  Button,
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  LoadingState,
  TextField,
} from '@/shared/ui';

import { TemplateCategories } from './TemplateCategories';
import {
  BoardTemplateEditor,
  DatabaseTemplateEditor,
  DocumentTemplateEditor,
} from './TemplateContentEditors';
import { TemplateEditDialog } from './TemplateEditDialog';
import styles from './Templates.module.css';
import { UseTemplateDialog } from './UseTemplateDialog';

/** Kinds with a content editor in Settings (the others can be renamed, copied, used, deleted). */
const EDITABLE: readonly TemplateKind[] = TEMPLATE_KINDS;
const CREATABLE: readonly TemplateKind[] = ['board', 'database', 'document'];

function parseKind(raw: string | null): TemplateKind {
  return TEMPLATE_KINDS.find((kind) => kind === raw) ?? TEMPLATE_KINDS[0] ?? 'board';
}

/** The content editor that fits the template's kind. */
function EditorFor({ template, onClose }: { template: TemplateSummary; onClose: () => void }) {
  switch (template.kind) {
    case 'board':
      return <BoardTemplateEditor templateId={template.id} onClose={onClose} />;
    case 'database':
      return <DatabaseTemplateEditor templateId={template.id} onClose={onClose} />;
    case 'document':
      return <DocumentTemplateEditor templateId={template.id} onClose={onClose} />;
    default:
      return <TemplateEditDialog templateId={template.id} onClose={onClose} />;
  }
}

/** The little a card says about a template, from its lightweight summary. */
function useFacts(item: TemplateSummary): string[] {
  const { t } = useTranslation();
  const d = item.details;
  const count = (key: string) => Number(d[key] ?? 0);
  if (item.kind === 'board') {
    return [
      t('templates.manage.summary.columns', { count: count('columns') }),
      d['include_tasks']
        ? `${t('templates.manage.summary.withTasks')}: ${String(count('tasks'))}`
        : t('templates.manage.summary.structureOnly'),
    ];
  }
  if (item.kind === 'database') {
    return [
      t('templates.manage.summary.tables', { count: count('tables') }),
      t('templates.manage.summary.fields', { count: count('columns') }),
      d['include_records']
        ? t('templates.manage.summary.records', { count: count('records') })
        : t('templates.manage.summary.structureOnly'),
    ];
  }
  if (item.kind === 'document') {
    const files = count('files');
    return [
      t('templates.manage.summary.richContent'),
      t('templates.manage.summary.blocks', { count: item.blocks }),
      ...(files > 0 ? [t('templates.manage.summary.files', { count: files })] : []),
    ];
  }
  return item.blocks > 0 ? [t('templates.manage.summary.blocks', { count: item.blocks })] : [];
}

/**
 * Settings -> Templates: the account's own templates in five categories (Boards, Databases, Notes,
 * Columns, Tasks), one list per category with search, and the same actions everywhere: create a
 * resource from it, edit (where an editor exists), rename, duplicate, delete. Templates are made
 * from "Save as template" in a resource's menu. Deleting one never removes what was already
 * created from it.
 */
export function TemplatesCard() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const kind = parseKind(params.get('kind'));
  const templates = useTemplates(kind);
  const [text, setText] = useState('');
  const [renaming, setRenaming] = useState<TemplateSummary | null>(null);
  const [editing, setEditing] = useState<TemplateSummary | null>(null);
  const [deleting, setDeleting] = useState<TemplateSummary | null>(null);
  const [using, setUsing] = useState<TemplateSummary | null>(null);
  const remove = useDeleteTemplate();
  const duplicate = useDuplicateTemplate();

  const select = (next: TemplateKind) => {
    setText('');
    setParams(
      (previous) => {
        const copy = new URLSearchParams(previous);
        copy.set('kind', next);
        return copy;
      },
      { replace: true },
    );
  };
  const needle = text.trim().toLocaleLowerCase();
  const shown = (templates.data ?? []).filter(
    (item) =>
      item.kind === kind &&
      (needle === '' ||
        item.name.toLocaleLowerCase().includes(needle) ||
        item.headline.toLocaleLowerCase().includes(needle)),
  );

  return (
    <div className={styles.manage}>
      <p className={styles.note}>{t('templates.manage.note')}</p>
      <TemplateCategories value={kind} onChange={select} />
      <div
        role="tabpanel"
        id="template-panel"
        aria-labelledby={`template-tab-${kind}`}
        className={styles.panel}
      >
        <input
          type="search"
          className={styles.search}
          aria-label={t('templates.manage.search')}
          placeholder={t('templates.manage.search')}
          value={text}
          maxLength={100}
          onChange={(event) => {
            setText(event.target.value);
          }}
        />
        {templates.isPending ? (
          <LoadingState />
        ) : templates.isError ? (
          <ErrorState
            error={templates.error}
            onRetry={() => {
              void templates.refetch();
            }}
          />
        ) : shown.length === 0 ? (
          <EmptyState
            title={
              needle
                ? t('templates.manage.noMatch', { text: text.trim() })
                : t(`templates.manage.empty.${kind}`)
            }
          />
        ) : (
          <ul className={styles.list} aria-label={t(`templates.tabs.${kind}`)}>
            {shown.map((item) => (
              <TemplateRow
                key={item.id}
                item={item}
                onUse={
                  CREATABLE.includes(item.kind)
                    ? () => {
                        setUsing(item);
                      }
                    : undefined
                }
                onEdit={
                  EDITABLE.includes(item.kind)
                    ? () => {
                        setEditing(item);
                      }
                    : undefined
                }
                onRename={() => {
                  setRenaming(item);
                }}
                onDuplicate={() => {
                  duplicate.mutate(item.id);
                }}
                onDelete={() => {
                  setDeleting(item);
                }}
              />
            ))}
          </ul>
        )}
        <FormError error={duplicate.error} />
      </div>
      {editing && (
        <EditorFor
          template={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {using && CREATABLE.includes(using.kind) && (
        <UseTemplateDialog
          template={using}
          kind={using.kind as 'board' | 'database' | 'document'}
          onClose={() => {
            setUsing(null);
          }}
        />
      )}
      {renaming && (
        <RenameDialog
          template={renaming}
          onClose={() => {
            setRenaming(null);
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={t('templates.manage.deleteTitle', { name: deleting.name })}
          message={t('templates.manage.deleteMessage')}
          confirmLabel={t('templates.manage.delete')}
          pending={remove.isPending}
          onConfirm={() => {
            remove.mutate(deleting.id, {
              onSettled: () => {
                setDeleting(null);
              },
            });
          }}
          onCancel={() => {
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}

function TemplateRow({
  item,
  onUse,
  onEdit,
  onRename,
  onDuplicate,
  onDelete,
}: {
  item: TemplateSummary;
  onUse: (() => void) | undefined;
  onEdit: (() => void) | undefined;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const facts = useFacts(item);
  return (
    <li className={styles.row}>
      <span className={styles.itemText}>
        <span className={styles.itemName}>{item.name}</span>
        {item.headline && item.headline !== item.name && (
          <span className={styles.itemMeta}>{item.headline}</span>
        )}
        {facts.length > 0 && (
          <ul className={styles.facts}>
            {facts.map((fact) => (
              <li key={fact} className={styles.fact}>
                {fact}
              </li>
            ))}
          </ul>
        )}
      </span>
      <span className={styles.rowActions}>
        {onUse && (
          <Button size="sm" variant="primary" onClick={onUse}>
            {t('templates.manage.use')}
          </Button>
        )}
        {onEdit && (
          <Button size="sm" onClick={onEdit}>
            {t('templates.manage.edit')}
          </Button>
        )}
        <Button size="sm" onClick={onRename}>
          {t('templates.manage.rename')}
        </Button>
        <Button size="sm" onClick={onDuplicate}>
          {t('templates.manage.duplicate')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          {t('templates.manage.delete')}
        </Button>
      </span>
    </li>
  );
}

function RenameDialog({ template, onClose }: { template: TemplateSummary; onClose: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateTemplate();
  const [name, setName] = useState(template.name);
  const code = isApiError(update.error) ? update.error.fieldErrors['name']?.[0] : undefined;
  const nameError = code
    ? t(`validation.${code}`, { defaultValue: t('validation.INVALID') })
    : undefined;
  return (
    <Dialog title={t('templates.manage.renameTitle')} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() === '' || update.isPending) return;
          update.mutate({ id: template.id, name: name.trim() }, { onSuccess: onClose });
        }}
      >
        <TextField
          label={t('templates.name')}
          value={name}
          maxLength={100}
          autoFocus
          error={nameError}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        {nameError === undefined && <FormError error={update.error} />}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={name.trim() === '' || update.isPending}>
            {t('common.save')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
